import { datadogRum } from "@datadog/browser-rum";

import type { QuickLauncherOpenTrigger } from "./quick-launcher.slice";
import type { QuickLauncherContextType } from "./use-quick-launcher-context.hook";

/**
 * Adoption telemetry for the quick launcher — the single writer of both
 * action names, so a dashboard or a RUM-generated metric has exactly one
 * place to look for what the payload means.
 *
 * Who the user is and which practice they are in is NOT in the payload on
 * purpose: `auth-slice` calls `datadogRum.setUser` / `setGlobalContext` after
 * `/me`, so every RUM action already carries `usr.id`, `usr.email`,
 * `usr.practice` and `practice_name`. Only `internal_user` is added here,
 * because RUM knows nothing about Django groups and adoption numbers need to
 * exclude Suno staff.
 *
 * Retention: RUM actions live ~30 days. The durable record is the RUM-generated
 * custom metric on `quick_launcher_used` (15 months), grouped by usr.email,
 * practice_name, action_id and context_type — which is why `action_id` must be
 * low-cardinality (see `QuickLauncherAction.telemetryId`).
 */
export const QUICK_LAUNCHER_OPENED_ACTION = "quick_launcher_opened";
export const QUICK_LAUNCHER_USED_ACTION = "quick_launcher_used";
/**
 * Separate from `quick_launcher_used` because running the Screenshot action and
 * actually exporting an image are different events: the first says the entry was
 * found, the second says the result was worth keeping. Every field is one of a
 * fixed handful of preset ids, so this stays safe to turn into a grouped metric.
 */
export const QUICK_LAUNCHER_SCREENSHOT_EXPORTED_ACTION =
  "quick_launcher_screenshot_exported";

/** How the highlighted row was run. */
export type QuickLauncherRunMethod = "keyboard" | "click";

interface QuickLauncherOpenedContext {
  trigger: QuickLauncherOpenTrigger;
  contextType: QuickLauncherContextType | null;
  internalUser: boolean;
}

interface QuickLauncherUsedContext {
  actionId: string;
  group: string;
  method: QuickLauncherRunMethod;
  contextType: QuickLauncherContextType | null;
  /** Length of the query when the action ran — 0 means "picked from the default list". */
  queryLength: number;
  internalUser: boolean;
}

export function trackQuickLauncherOpened(
  context: QuickLauncherOpenedContext
): void {
  try {
    datadogRum.addAction(QUICK_LAUNCHER_OPENED_ACTION, {
      trigger: context.trigger,
      context_type: context.contextType,
      internal_user: context.internalUser
    });
  } catch {
    // Telemetry must never break the launcher.
  }
}

export function trackQuickLauncherUsed(
  context: QuickLauncherUsedContext
): void {
  try {
    datadogRum.addAction(QUICK_LAUNCHER_USED_ACTION, {
      action_id: context.actionId,
      group: context.group,
      method: context.method,
      context_type: context.contextType,
      query_length: context.queryLength,
      internal_user: context.internalUser
    });
  } catch {
    // Telemetry must never break the launcher.
  }
}

/** How a framed screenshot left the dialog. */
export type QuickLauncherScreenshotExportMethod = "copy" | "download";

interface QuickLauncherScreenshotExportedContext {
  method: QuickLauncherScreenshotExportMethod;
  /** A `ScreenshotBackgroundId` — a preset name, never a colour value. */
  backgroundId: string;
  /** A `ScreenshotPaddingId`. */
  paddingId: string;
  contextType: QuickLauncherContextType | null;
  /** Whether patient names were masked in the capture. */
  privacyMode: boolean;
  internalUser: boolean;
}

export function trackQuickLauncherScreenshotExported(
  context: QuickLauncherScreenshotExportedContext
): void {
  try {
    datadogRum.addAction(QUICK_LAUNCHER_SCREENSHOT_EXPORTED_ACTION, {
      method: context.method,
      background_id: context.backgroundId,
      padding_id: context.paddingId,
      context_type: context.contextType,
      privacy_mode: context.privacyMode,
      internal_user: context.internalUser
    });
  } catch {
    // Telemetry must never break the launcher.
  }
}
